import React, { useState, useRef, useEffect } from "react";
import { Smartphone, LockKeyhole, LogIn, Loader2, ArrowRight, ArrowLeft, ShieldCheck, X } from "lucide-react";
import { useAuth, cutoverBannerState, dismissCutoverBanner } from "../lib/auth";
import FormError from "./ui/FormError";

/**
 * Mobile-number + 6-digit PIN login.
 *
 * Step 1 ("mobile"): operator enters their 10-digit number.
 *   - We POST /auth/mobile/check; back-end tells us whether the mobile is
 *     registered AND whether a PIN has been set.
 * Step 2 ("login"): registered + pin_set -> enter PIN.
 * Step 2 ("register"): registered + !pin_set -> set a fresh PIN (first time).
 * Step 2 ("denied"): not registered / not approved -> "Contact your coach".
 *
 * Token is stored in localStorage with 30-day expiry via the existing
 * AuthProvider — user does not have to log in again until that expires.
 */
export default function LoginScreen() {
  const { mobileCheck, mobileLogin, mobileRegister } = useAuth();
  const [step, setStep] = useState("mobile"); // mobile | login | register | denied
  const [mobile, setMobile] = useState("");
  const [knownName, setKnownName] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Only shown to users who had a legacy localStorage token — first-time
  // users won't see this "we upgraded security, please sign in again"
  // banner because there's no session to migrate.
  const [showCutover, setShowCutover] = useState(() => cutoverBannerState() === "pending");
  const pinRef = useRef(null);
  const mobileRef = useRef(null);

  useEffect(() => {
    if (step === "login" || step === "register") pinRef.current?.focus();
    if (step === "mobile") mobileRef.current?.focus();
  }, [step]);

  const checkMobile = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const digits = mobile.replace(/\D/g, "").slice(-10);
      if (digits.length !== 10) throw new Error("Enter a 10-digit mobile number");
      const r = await mobileCheck(digits);
      setMobile(digits);
      setKnownName(r.name || "");
      if (!r.exists || !r.approved) setStep("denied");
      else if (r.pin_set) setStep("login");
      else setStep("register");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err?.message || "Could not check mobile number"));
    } finally {
      setBusy(false);
    }
  };

  const submitLogin = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!pin) throw new Error("Enter your PIN");
      await mobileLogin(mobile, pin, true);
      dismissCutoverBanner();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid PIN. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!/^\d{6,10}$/.test(pin)) throw new Error("PIN must be 6-10 digits");
      if (pin !== confirmPin) throw new Error("PINs don't match");
      await mobileRegister(mobile, pin, name || knownName, true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err?.message || "Could not set your PIN"));
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setError("");
    setPin("");
    setConfirmPin("");
    setStep("mobile");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="login-screen">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.18]"
             style={{ backgroundImage: "url('https://images.pexels.com/photos/9716320/pexels-photo-9716320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#040D1A]/40 via-[#040D1A]/70 to-[#040D1A]" />
      </div>

      <div className="w-full max-w-md card p-7 md:p-9 fade-up">
        <div className="flex flex-col items-center mb-6">
          <img
            src="/branding/ych-logo.png"
            alt="The Yacht Club of Hyderabad"
            className="w-44 md:w-52 drop-shadow-[0_4px_18px_rgba(0,229,255,0.18)]"
            data-testid="login-logo"
          />
          <div className="text-[11px] text-slate-400 tracking-[0.22em] uppercase mt-2">
            Athlete Health · {step === "register" ? "Set your PIN" : step === "denied" ? "Access pending" : "Sign in"}
          </div>
        </div>

        {showCutover && (
          <div
            className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded border border-cyan-400/30 bg-cyan-400/5 text-[12px] text-cyan-100"
            data-testid="login-cutover-banner"
          >
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-cyan-300" />
            <div className="flex-1 leading-relaxed">
              <strong className="text-cyan-200">Security upgraded.</strong>{" "}
              We&apos;ve moved your login to a safer cookie-based session.
              Please sign in again once — you won&apos;t have to do this again.
            </div>
            <button
              type="button"
              onClick={() => { dismissCutoverBanner(); setShowCutover(false); }}
              className="text-cyan-300/70 hover:text-cyan-100"
              aria-label="Dismiss security notice"
              data-testid="login-cutover-banner-dismiss"
            >
              <X size={13} />
            </button>
          </div>
        )}

        {step === "mobile" && (
          <form onSubmit={checkMobile} data-testid="login-mobile-form">
            <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Mobile number</label>
            <div className="relative mb-4">
              <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                ref={mobileRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data"
                placeholder="9849002111"
                maxLength={15}
                data-testid="login-mobile-input"
              />
            </div>

            {error && <div className="mb-4"><FormError message={error} testId="login-error" /></div>}

            <button type="submit" disabled={busy} className="w-full pill active justify-center py-2.5" data-testid="login-mobile-continue-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              <span>{busy ? "Checking…" : "Continue"}</span>
            </button>

            <div className="mt-6 pt-5 border-t border-[#1E304B] text-[11px] text-slate-500 leading-relaxed">
              Access is invite-only. Your mobile number must be pre-authorised by an admin.
              First-time sign-in lets you choose a 6-10 digit PIN you&apos;ll use thereafter.
            </div>
          </form>
        )}

        {step === "login" && (
          <form onSubmit={submitLogin} data-testid="login-pin-form">
            <button type="button" onClick={back} className="mb-3 text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="login-back-btn">
              <ArrowLeft size={11} /> Different mobile
            </button>
            <div className="mb-4 px-3 py-2 rounded border border-[#1E304B] bg-[#0E1A2E] text-[12px] text-slate-300 font-mono-data">
              Hi {knownName || "there"} · <span className="text-cyan-300">{mobile}</span>
            </div>

            <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Your PIN</label>
            <div className="relative mb-4">
              <LockKeyhole size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                ref={pinRef}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="current-password"
                required
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="w-full pl-9 pr-3 py-2.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data tracking-widest"
                placeholder="••••••"
                maxLength={10}
                data-testid="login-pin-input"
              />
            </div>

            {error && <div className="mb-4"><FormError message={error} testId="login-error" /></div>}

            <button type="submit" disabled={busy} className="w-full pill active justify-center py-2.5" data-testid="login-submit-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
              <span>{busy ? "Signing in…" : "Sign in"}</span>
            </button>
            <div className="mt-3 text-[10px] text-slate-500 text-center">Stays signed in for 30 days on this device.</div>
          </form>
        )}

        {step === "register" && (
          <form onSubmit={submitRegister} data-testid="login-register-form">
            <button type="button" onClick={back} className="mb-3 text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="login-back-btn">
              <ArrowLeft size={11} /> Different mobile
            </button>
            <div className="mb-4 px-3 py-2 rounded border border-emerald-500/30 bg-emerald-500/10 text-[12px] text-emerald-200">
              <span className="font-mono-data text-emerald-300">{mobile}</span> is approved.
              Set a PIN — you&apos;ll use it every time you sign in.
            </div>

            {!knownName && (
              <>
                <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Your name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mb-4 px-3 py-2.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
                  placeholder="Coach Suheim"
                  data-testid="login-name-input"
                />
              </>
            )}

            <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Choose a 4-10 digit PIN</label>
            <input
              ref={pinRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="w-full mb-3 px-3 py-2.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data tracking-widest"
              placeholder="••••••"
              minLength={6}
              maxLength={10}
              data-testid="login-new-pin-input"
            />
            <label className="block text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Confirm PIN</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="w-full mb-4 px-3 py-2.5 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data tracking-widest"
              placeholder="••••••"
              maxLength={10}
              data-testid="login-confirm-pin-input"
            />

            {error && <div className="mb-4"><FormError message={error} testId="login-error" /></div>}

            <button type="submit" disabled={busy} className="w-full pill active justify-center py-2.5" data-testid="login-register-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
              <span>{busy ? "Setting PIN…" : "Set PIN & sign in"}</span>
            </button>
            <div className="mt-3 text-[10px] text-slate-500 text-center">
              Keep your PIN private. If you forget it, ask your admin to reset it.
            </div>
          </form>
        )}

        {step === "denied" && (
          <div data-testid="login-denied">
            <button type="button" onClick={back} className="mb-3 text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="login-back-btn">
              <ArrowLeft size={11} /> Try a different mobile
            </button>
            <div className="px-3 py-4 rounded border border-amber-500/30 bg-amber-500/10 text-[13px] text-amber-100 leading-relaxed">
              <strong>This mobile is not authorised.</strong>
              <div className="mt-2 text-[12px] text-amber-200">
                <span className="font-mono-data">{mobile}</span> has not been added to the squad
                by an admin yet. Please contact your coach / admin and ask them to add your
                number under <em>Users</em>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
